
import React, { createContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { User, UserRole, UserTheme, NavigationLayout } from '../types';
import * as api from '../services/api';
import { supabase } from '../services/supabase';
import { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { CrownIcon } from '../components/ui/icons';

export interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (email: string, password: string) => Promise<string | null>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  loading: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const CACHED_USER_KEY = 'fairyrealm_cached_user_session';
const getCachedUser = (): User | null => {
  try {
    const raw = localStorage.getItem(CACHED_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUserState] = useState<User | null>(getCachedUser);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const [showLoadingFallback, setShowLoadingFallback] = useState(false);

  const setUser = (newUser: User | null) => {
    setUserState(newUser);
    try {
      if (newUser) {
        localStorage.setItem(CACHED_USER_KEY, JSON.stringify(newUser));
      } else {
        localStorage.removeItem(CACHED_USER_KEY);
      }
    } catch {
      // Storage access might be restricted in some environments
    }
  };

  const refresh = async () => {
    console.log("AuthProvider: Manual refresh requested");
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    if (currentSession?.user) {
        // Bypass the ref check for manual refresh
        const oldRef = isRefreshingRef.current;
        isRefreshingRef.current = false;
        await refreshUserRole(currentSession.user);
        isRefreshingRef.current = oldRef;
    }
  };

  useEffect(() => {
    const activeTheme = user?.theme || 'amethyst';
    console.log("AuthProvider: Applying theme:", activeTheme);
    
    // Remove all existing theme classes
    Array.from(document.body.classList)
      .filter(cls => cls.startsWith('theme-'))
      .forEach(cls => document.body.classList.remove(cls));
    Array.from(document.documentElement.classList)
      .filter(cls => cls.startsWith('theme-'))
      .forEach(cls => document.documentElement.classList.remove(cls));
    
    // Add the new one
    document.body.classList.add(`theme-${activeTheme}`);
    document.documentElement.classList.add(`theme-${activeTheme}`);
  }, [user?.theme]);

  // Safety timeout to prevent infinite loading
  useEffect(() => {
      if (loading) {
          const timer = setTimeout(() => {
              setShowLoadingFallback(true);
          }, 3000); 
          return () => clearTimeout(timer);
      } else {
          setShowLoadingFallback(false);
      }
  }, [loading]);

  useEffect(() => {
    // 1. Check active session on load
    const initSession = async () => {
        console.log("AuthProvider: Initializing session...");
        try {
            const sessionPromise = supabase.auth.getSession();
            const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('Session Timeout')), 30000));
            
            const { data: { session: currentSession }, error = null } = (await Promise.race([sessionPromise, timeoutPromise])) as { data: { session: Session | null }, error: Error | null };
            if (error) throw error;
            
            setSession(currentSession);
            if (currentSession?.user) {
                await refreshUserRole(currentSession.user);
            } else {
                setLoading(false);
            }
        } catch (error: unknown) {
            console.error("Error initializing session:", error);
            setLoading(false);
        }
    };
    initSession();

    // 2. Listen for changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      setSession(currentSession);
      if (currentSession?.user) {
          if (!user || user.email !== currentSession.user.email) {
               await refreshUserRole(currentSession.user);
          }
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setLoading(false);
      } else {
         if (!currentSession) {
             setUser(null);
             setLoading(false);
         }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Proactive Keep-Alive & Session Refresh to prevent logouts during inactivity
  useEffect(() => {
    if (!session) return;

    console.log("AuthProvider: Setting up keep-alive interval");
    const interval = setInterval(async () => {
        console.log("AuthProvider: Proactive session check...");
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        
        if (error) {
            console.error("AuthProvider: Session check error:", error);
            // If it's a fatal error, the onAuthStateChange on line 102 will eventually handle it
            // but we try one manual refresh if it's potentially just a glitch
            if (error.message.includes('expired')) {
                supabase.auth.refreshSession();
            }
        } else if (currentSession) {
            setSession(currentSession);
        }
    }, 15 * 60 * 1000); // Check every 15 minutes

    return () => clearInterval(interval);
  }, [session]);

  const isRefreshingRef = React.useRef(false);

  const refreshUserRole = async (currentUser: SupabaseUser) => {
      console.log("AuthProvider: Refreshing user role for", currentUser.email);
      if (isRefreshingRef.current) {
          console.log("AuthProvider: Already refreshing, skipping...");
          return;
      }
      isRefreshingRef.current = true;
      
      setConnectionError(false);
      try {
        if (!currentUser.email) {
            setLoading(false);
            isRefreshingRef.current = false;
            return;
        }
        
        // Timeout for API call (longer than internal query timeout in api.ts to allow retries)
        const detailsPromise = api.getAppUserDetails(currentUser.email);
        const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('Details Timeout')), 100000));
        
        const details = (await Promise.race([detailsPromise, timeoutPromise])) as { 
          role: UserRole | null, 
          clientId?: string, 
          theme?: UserTheme, 
          navigationLayout?: NavigationLayout,
          telegramChatId?: string,
          error?: string 
        };
        
        console.log("AuthProvider: Details fetched", { email: currentUser.email, role: details.role, telegramId: details.telegramChatId, navLayout: details.navigationLayout });
        
        if (!details.role) {
             console.warn("User denied access:", details.error);
             await supabase.auth.signOut();
             setUser(null);
             setSession(null);
        } else {
             setUser({
                id: currentUser.id,
                email: currentUser.email,
                role: details.role,
                clientId: details.clientId,
                theme: details.theme || UserTheme.AMETHYST,
                navigationLayout: details.navigationLayout || NavigationLayout.SIDEBAR,
                telegramChatId: details.telegramChatId
            });

            if (details.role === UserRole.SUB && details.clientId) {
                api.updateLastLogin(details.clientId).catch(e => console.warn("Failed to update last activity", e));
            }
        }
        } catch (error) {
          const errorMessage = (error instanceof Error ? error.message : String(error)).toLowerCase();
          
          const isNetworkError = 
                errorMessage.includes('failed to fetch') || 
                errorMessage.includes('networkerror') || 
                errorMessage.includes('timeout') ||
                errorMessage.includes('load failed') ||
                errorMessage === 'offline';
          
          if (isNetworkError) {
              console.warn("Network issue during user role refresh:", errorMessage);
              const dominaEmails = ['cat@fairyrealm.com', 'cat@fairyrule.com', 'catherineisallaround@gmail.com'];
              if (currentUser.email && dominaEmails.includes(currentUser.email.toLowerCase())) {
                  console.log("Using Domina identity fallback during network interruption");
                  setUser({
                      id: currentUser.id,
                      email: currentUser.email,
                      role: UserRole.DOMINA,
                      theme: UserTheme.AMETHYST,
                      navigationLayout: NavigationLayout.SIDEBAR
                  });
              } else {
                  const cached = getCachedUser();
                  if (cached && cached.email === currentUser.email) {
                      console.log("Using cached identity fallback during network interruption");
                      setUser(cached);
                  } else {
                      setConnectionError(true);
                  }
              }
          } else {
              console.error("Failed to refresh user role:", error);
              // Only sign out if we are SURE the session is invalid or the query is rejected
              const isFatal = errorMessage.includes('invalid jwt') || 
                            errorMessage.includes('user not found') || 
                            errorMessage.includes('not allowlisted') ||
                            errorMessage.includes('jwt expired');

              if (isFatal) {
                  console.warn("Fatal auth error, signing out:", errorMessage);
                  try {
                      await supabase.auth.signOut();
                  } catch (e: unknown) { /* ignore */ }
                  setUser(null);
                  setSession(null);
              } else {
                  console.warn("Non-fatal refresh error, keeping session for now:", errorMessage);
              }
          }
      } finally {
          setLoading(false);
          isRefreshingRef.current = false;
      }
  };

  const login = async (email: string, password: string): Promise<string | null> => {
    setLoading(true);
    setConnectionError(false);
    try {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        
        if (error) {
          setLoading(false);
          if (error.message === 'Invalid login credentials') return 'Ungültige E-Mail oder falsches Passwort.';
          if (error.message.includes('Email not confirmed')) return 'Bitte bestätige zuerst deine E-Mail-Adresse.';
          if (error.message.includes('Too many requests') || error.status === 429) return 'Zu viele Versuche. Bitte kurz warten.';
          return 'Anmeldung fehlgeschlagen: ' + error.message;
        }
        
        if (data.user && data.user.email) {
             try {
                 const details = await api.getAppUserDetails(data.user.email);
                 if (!details.role) {
                     await supabase.auth.signOut();
                     setLoading(false);
                     return details.error || 'Zugriff verweigert.';
                 }
                 if (details.role === UserRole.SUB && details.clientId) {
                     await api.updateLastLogin(details.clientId);
                 }
             } catch (err) {
                 const message = err instanceof Error ? err.message : String(err);
                 if (message.toLowerCase().includes('fetch')) {
                     setLoading(false);
                     return 'Verbindungsproblem. Bitte Internet prüfen.';
                 }
             }
        }
        return null;
    } catch (err) {
        setLoading(false);
        return 'Technischer Fehler.';
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
        await supabase.auth.signOut();
    } finally {
        setUser(null);
        setSession(null);
        setLoading(false);
    }
  };

  const forceLogout = async () => {
      setLoading(true);
      
      // 1. Supabase abmelden versuchen (mit Timeout)
      try { 
          const signOutPromise = supabase.auth.signOut();
          const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 2000));
          await Promise.race([signOutPromise, timeoutPromise]);
      } catch (e: unknown) {
          console.warn("Soft logout failed or timed out, forcing storage wipe.");
      }
      
      // 2. LocalStorage radikal säubern (löscht Tokens & Cache)
      try {
          localStorage.clear();
          sessionStorage.clear();
      } catch (e: unknown) {
          console.error("Failed to clear storage", e);
      }
      
      // 3. States zurücksetzen
      setUser(null);
      setSession(null);
      setLoading(false);
      
      // 4. Harter Redirect zur Login-Seite
      const loginUrl = window.location.origin + window.location.pathname + '#/login';
      window.location.href = loginUrl;
      window.location.reload();
  };

  const handleRetry = () => {
      setLoading(true);
      setConnectionError(false);
      if (session?.user) {
          refreshUserRole(session.user);
      } else {
          window.location.reload();
      }
  };

  const value = useMemo(() => ({ 
    isAuthenticated: !!session && !!user, 
    user, 
    login, 
    logout, 
    refresh,
    loading 
  }), [session, user, login, logout, refresh, loading]);

  if (connectionError) {
      return (
          <div className="h-screen w-screen flex flex-col items-center justify-center bg-background p-4 text-center">
              <CrownIcon className="w-16 h-16 text-primary mb-4 opacity-50" />
              <h2 className="text-xl font-bold text-red-400 mb-2">Verbindungsproblem</h2>
              <p className="text-text-secondary mb-6 max-w-xs mx-auto">
                  Das Fairyrealm kann aktuell keine Verbindung zum Äther herstellen.
              </p>
              <div className="space-y-3 w-full max-w-xs">
                  <button onClick={handleRetry} className="w-full px-4 py-3 bg-primary text-white rounded-lg font-semibold shadow-glow">Erneut versuchen</button>
                  <button onClick={forceLogout} className="w-full px-4 py-3 bg-surface border border-border text-text-secondary rounded-lg">Abmelden</button>
              </div>
          </div>
      );
  }

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
          <div className="h-screen w-screen flex flex-col items-center justify-center bg-background text-text-secondary gap-6 p-4">
              <div className="flex flex-col items-center gap-2 animate-pulse">
                  <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-xl font-heading uppercase tracking-widest">Wird beschworen...</span>
              </div>
              
              {showLoadingFallback && (
                  <div className="flex flex-col items-center gap-3 animate-fade-in bg-surface p-6 rounded-lg border border-red-900/50 shadow-xl max-w-sm text-center">
                      <p className="text-sm text-red-200">Die Synchronisation dauert länger als gewohnt.</p>
                      <button 
                        onClick={(e) => {
                            e.preventDefault();
                            forceLogout();
                        }} 
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-semibold w-full cursor-pointer active:scale-95 transition-all"
                      >
                        Zurück zum Start
                      </button>
                  </div>
              )}
          </div>
      ) : children}
    </AuthContext.Provider>
  );
};
